/**
 * How a visitor arrived for THIS visit — on foot, or in a vehicle.
 *
 * A visitor's registered vehicle was attached to every visit automatically, and the only way to
 * detach it was to realise the plate row was a toggle and click it off. Even then nothing recorded
 * that they came on foot: it looked exactly like a visit where the operator never touched the
 * section. The gate log therefore showed a car that was never on the property.
 *
 * The choice is only worth presenting to someone who HAS a car on file. For everyone else "no
 * vehicle" is the ordinary case, and an "a pé" row on every visit is noise.
 */

export type VisitArrivalMode = 'a_pe' | 'veiculo';

const GUEST_ROLES = new Set(['visitor', 'service_provider']);

/** Whether a visit is in progress right now — the Visitantes Ativos state. */
export function isVisitorInside(roles: any[] | null | undefined): boolean {
  return (roles ?? []).some(
    (role: any) =>
      GUEST_ROLES.has(role?.role_type) && role?.role_metadata?.visitor_state === 'inside',
  );
}

export function offersOnFootChoice(input: {
  /** Vehicles on the person's own record. */
  registeredVehicleCount: number;
  /**
   * Vehicles attached to THIS visit. Counted separately because they need not be on the person's
   * record at all: "Repetir Visita" carries the previous visit's plate across, and a plate typed
   * at the desk behaves the same way. Gating on the record alone hid the option for exactly the
   * cases where a plate was already filled in and the operator needed to say "not today".
   */
  attachedVehicleCount?: number;
  roles: any[] | null | undefined;
}): boolean {
  if (input.registeredVehicleCount < 1 && (input.attachedVehicleCount ?? 0) < 1) return false;
  // Once they are inside the arrival has already happened, so there is nothing left to declare
  // about how it happened — offering the choice there invites an edit to a record of the past.
  return !isVisitorInside(input.roles);
}

/**
 * What to stamp on the visit, given what the operator chose.
 *
 * Returns nothing when the person has no vehicle on file AND no plate was typed: no choice was
 * ever put in front of the operator, and writing "a_pe" would read as a declaration they did not
 * make.
 */
export function arrivalModeFor(input: {
  hasRegisteredVehicles: boolean;
  /** A plate this visit had carried (from the previous visit, or typed) before it was cleared. */
  hadAttachedVehicle?: boolean;
  selectedPlate?: string;
}): VisitArrivalMode | undefined {
  if (input.selectedPlate && input.selectedPlate.trim()) return 'veiculo';
  return input.hasRegisteredVehicles || input.hadAttachedVehicle ? 'a_pe' : undefined;
}

/** A vehicle this visit can be assigned, and whether it currently is. */
export interface VisitVehicleOption {
  plate: string;
  model?: string;
  color?: string;
  /** Extra detail a registered vehicle carries; absent for a plate typed at the desk. */
  brand?: string;
  year?: number;
  isActive?: boolean;
  /** Where it came from: the person's record, or this visit (carried over, or typed). */
  source: 'registered' | 'visit';
  isSelected: boolean;
}

const plateKey = (plate: string | undefined | null): string =>
  String(plate ?? '').replace(/\s+/g, '').toUpperCase();

/**
 * Every vehicle the operator may pick for this visit, with the selection marked ON them.
 *
 * Derived rather than stored, because the two used to be one list: the selection WAS the
 * catalogue, so choosing "Entrando a pé" deleted any plate that was not on the person's record.
 * A visitor whose plate had been carried over by "Repetir Visita" was left with no vehicle at all
 * and no way back to it. Deselecting must never remove an option.
 */
export function visitVehicleOptions(input: {
  personVehicles: Array<{ plate?: string; model?: string; color?: string; brand?: string; year?: number; is_active?: boolean }>;
  /** Plates this visit has picked up — carried over, or typed at the desk. */
  offeredPlates: Array<{ plate?: string; model?: string; color?: string }>;
  selected: Array<{ plate?: string }>;
}): VisitVehicleOption[] {
  const selectedKeys = new Set(input.selected.map(v => plateKey(v.plate)).filter(Boolean));
  const seen = new Set<string>();
  const options: VisitVehicleOption[] = [];

  for (const vehicle of input.personVehicles ?? []) {
    const key = plateKey(vehicle.plate);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    options.push({
      plate: vehicle.plate as string,
      model: vehicle.model,
      color: vehicle.color,
      brand: vehicle.brand,
      year: vehicle.year,
      isActive: vehicle.is_active,
      source: 'registered',
      isSelected: selectedKeys.has(key),
    });
  }

  for (const vehicle of input.offeredPlates ?? []) {
    const key = plateKey(vehicle.plate);
    // A plate the visit picked up that IS on the record is the same vehicle, not a second one.
    if (!key || seen.has(key)) continue;
    seen.add(key);
    options.push({
      plate: vehicle.plate as string,
      model: vehicle.model,
      color: vehicle.color,
      source: 'visit',
      isSelected: selectedKeys.has(key),
    });
  }

  return options;
}
